import { CELL_WIDTH } from '@components/export/export.config';
import * as GC from '@grapecity/spread-sheets';
import { IProduct, IProductConcentrationDetails, IProductGroup } from '@models/product';
import {
  borders,
  colours,
  CURRENCY_FORMATTER,
  DASH,
  FONT_NAME,
  FONT_SIZE,
  PERCENTAGE_FORMATTER_2_DECIMALS
} from '@shared/spread/spread-settings';
import { getDateForDisplay } from '../helpers';

const COLUMN_HEADERS = ['Total Avg Balance', 'Top 10 AVG Balance', 'Top 10% Of Total Balance'];
const TABLE_TOTAL_COLUMNS = COLUMN_HEADERS.length + 1;
const HEADERS_ROW_HEIGHT = 40;

const getFont = (option: 'small' | 'medium' | 'large' | 'x-large', bold: boolean = false) => {
  let multiplier: number;
  switch (option) {
    case 'small':
      multiplier = 0.8;
      break;
    case 'medium':
      multiplier = 1;
      break;
    case 'large':
      multiplier = 1.1;
      break;
    case 'x-large':
      multiplier = 1.2;
      break;
  }

  return `${bold ? 'bold ' : ''}${Math.round(Number(FONT_SIZE) * multiplier)}pt ${FONT_NAME}`;
};

export const createConcentrationReportExport = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  colIndex: number,
  products: IProduct[],
  productGroups: IProductGroup[],
  concentrationDetails: IProductConcentrationDetails[],
  lastObservationDate: Date
) => {
  // Set column width
  const colWidth = Math.round(CELL_WIDTH * 3.5);
  for (let i = 0; i < TABLE_TOTAL_COLUMNS; i++) {
    sheet.setColumnWidth(colIndex + i, colWidth); // TODO: Use CELL_WIDTH for all the columns and CELL_HEIGHT for all the rows
  }

  // As Of Date
  rowIndex = populateAsOfDate(sheet, rowIndex, colIndex, lastObservationDate);
  rowIndex += 1;

  productGroups = productGroups.sort((groupA, groupB) => groupA.GroupName.localeCompare(groupB.GroupName, 'en', { sensitivity: 'base' }));

  products = products.sort((productA, productB) => productA.ProductName.localeCompare(productB.ProductName, 'en', { sensitivity: 'base' }));

  // Populate data By Product Group
  productGroups.forEach(group => {
    const sameGroupProductIds: number[] = [];
    products.forEach(({ ProductGroupId, ProductId }) => {
      if (ProductGroupId === group.ProductGroupId) {
        sameGroupProductIds.push(ProductId);
      }
    });

    if (sameGroupProductIds.length > 0) {
      // Product Group header
      rowIndex = addProductGroupHeaders(sheet, rowIndex, colIndex, group);
      // Display the products concentration data
      const filteredConcentrationDetails = concentrationDetails.filter(({ ProductId }) => sameGroupProductIds.includes(ProductId));
      rowIndex = addProductGroupConcentrationData(sheet, rowIndex, colIndex, filteredConcentrationDetails);
      rowIndex += 2;
    }
  });
  sheet.zoom(0.85);
};

function addProductGroupHeaders(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, colIndex: number, group: IProductGroup): number {
  // Group Name
  sheet.getCell(rowIndex, colIndex).text(group.GroupName);
  // Column Headers
  COLUMN_HEADERS.forEach((header, ind) => sheet.getCell(rowIndex, colIndex + 1 + ind).text(header));

  // Styling
  const productHeaderRange = sheet.getRange(rowIndex, colIndex, 1, TABLE_TOTAL_COLUMNS);
  productHeaderRange
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.right)
    .font(getFont('x-large', true));

  productHeaderRange.setBorder(borders.DARK_GREY_THIN, { outline: true });
  productHeaderRange.setBorder(borders.BLACK_MEDIUM, { bottom: true });
  // Left align product group names
  sheet.getRange(rowIndex, colIndex, 1, 1).hAlign(GC.Spread.Sheets.HorizontalAlign.left);

  sheet.setRowHeight(productHeaderRange.row, HEADERS_ROW_HEIGHT);

  rowIndex++;
  return rowIndex;
}

function addProductGroupConcentrationData(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex,
  colIndex: number,
  concentrationDetails: IProductConcentrationDetails[]
): number {
  concentrationDetails.forEach((concentrationDetail, index) => {
    // Product Name
    sheet.getCell(rowIndex, colIndex).text(concentrationDetail.ProductName ? concentrationDetail.ProductName : DASH);
    // Total Avg. Balance
    sheet.getCell(rowIndex, colIndex + 1).value(concentrationDetail.TotalAvgBalance ? concentrationDetail.TotalAvgBalance : DASH);
    // Top 10 Avg Balances
    sheet.getCell(rowIndex, colIndex + 2).value(concentrationDetail.Top10AvgBalance ? concentrationDetail.Top10AvgBalance : DASH);
    // Top 10% of total balance
    const topTenPercent = concentrationDetail.Top10Accounts
      ? concentrationDetail.TotalAvgBalance === 0 || concentrationDetail.TotalAvgBalance === null
        ? 1
        : concentrationDetail.Top10AvgBalance / concentrationDetail.TotalAvgBalance
      : DASH;
    sheet.getCell(rowIndex, colIndex + 3).value(topTenPercent);

    rowIndex++;
  });

  // Add Formatters
  {
    const avgBalanceRange = sheet.getRange(rowIndex - concentrationDetails.length, colIndex + 1, concentrationDetails.length, 2);
    avgBalanceRange.formatter(CURRENCY_FORMATTER);
    const totalBalanceRange = sheet.getRange(rowIndex - concentrationDetails.length, colIndex + 3, concentrationDetails.length, 1);
    totalBalanceRange.formatter(PERCENTAGE_FORMATTER_2_DECIMALS);
  }

  // Styling
  {
    const productDataRange = sheet.getRange(rowIndex - concentrationDetails.length, colIndex, concentrationDetails.length, 4);
    productDataRange
      .vAlign(GC.Spread.Sheets.VerticalAlign.center)
      .hAlign(GC.Spread.Sheets.HorizontalAlign.right)
      .setBorder(borders.DARK_GREY_THIN, { outline: true });
    // Left align product names
    sheet.getRange(productDataRange.row, productDataRange.col, productDataRange.rowCount, 1).hAlign(GC.Spread.Sheets.HorizontalAlign.left);
  }

  return rowIndex;
}

const populateAsOfDate = (sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, colIndex: number, lastObservationDate: Date): number => {
  const takenRows = 2;

  sheet
    .getCell(rowIndex, colIndex)
    .text('As Of Date')
    .font(getFont('x-large', true));
  sheet.getCell(rowIndex, colIndex + 1).text(getDateForDisplay(lastObservationDate));

  // Styling
  const asOfDateRange = sheet.getRange(rowIndex, colIndex, 1, takenRows);
  asOfDateRange
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .backColor(colours.LIGHT_BLUE);
  asOfDateRange.setBorder(borders.BLACK_MEDIUM, { outline: true });

  rowIndex++;
  return rowIndex;
};
